import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Form, FormGroup } from "reactstrap";
import { Btn, H4, P } from "../../../../AbstractElements";
import { Link } from "react-router-dom";
import "../../login.scss";

import {
  CreateAccount,
  Email as EmailLabel,
  Password as PasswordLabel,
  registerPath,
  SignIn,
  SignInAccount,
} from "../../../../utils/Constant";

import { ILoginFormProps } from "../../types";
import { loginValidationSchema } from "../../../employeeManagement/validationSchema";

import {
  BaseFieldConfig,
  DynamicFieldRenderer,
} from "../../../../CommonElements/DynamicFieldRenderer";
import { loginFormFields } from "../../fields/loginFields";
import IconComponent from "../../../../CommonElements/IconComponent";

type LoginValues = {
  email: string;
  password: string;
};

const defaultValues: LoginValues = { email: "", password: "" };

const LoginForm = ({
  handleSubmit: onSubmitFromProps,
  showPassword,
  isLoading,
}: ILoginFormProps) => {
  const methods = useForm<LoginValues>({
    resolver: yupResolver(loginValidationSchema as any),
    defaultValues,
    mode: "onChange",
  });

  const { handleSubmit } = methods;

  // Dynamically flip password field type if you want eye-toggle behavior
  const fields: BaseFieldConfig[] = useMemo(() => {
    const typedType: BaseFieldConfig["type"] = showPassword
      ? "text"
      : "password";
    return loginFormFields.map((f) =>
      f.name === "password" ? { ...f, type: typedType } : f
    );
  }, [showPassword]);

  const onSubmit = (vals: LoginValues) => {
    onSubmitFromProps(vals);
  };

  return (
    <div>
      <FormProvider {...methods}>
        <Form className="theme-form" onSubmit={handleSubmit(onSubmit)}>
          <H4>{SignInAccount}</H4>
          <P>Enter your email & password to login</P>

          {/* Email */}
          {DynamicFieldRenderer({ ...fields[0], label: EmailLabel }, [])}

          {/* Password (+ optional toggle) */}
          <div className="position-relative">
            {DynamicFieldRenderer({ ...fields[1], label: PasswordLabel }, [])}
          </div>

          {/* Submit */}
          <FormGroup className="mb-0 position-relative">
            <div className="text-end mt-4">
              <Btn type="submit" color="primary" block disabled={isLoading}>
                {isLoading ? (
                  <>
                    <IconComponent className="fa fa-spinner fa-spin text-white" />{" "}
                    Please wait....
                  </>
                ) : (
                  SignIn
                )}
              </Btn>
            </div>
          </FormGroup>

          {/* Redirect to Sign Up */}
          <P className="mt-4 mb-0 text-center">
            {"Don't have account?"}
            <Link className="ms-2" to={registerPath}>
              {CreateAccount}
            </Link>
          </P>

          {/* If needed later:
          <div className="d-flex justify-content-center mt-2">
            <a
              className="text-center"
              href="http://adminmis.iifm.co.in/reset-password"
              target="_blank"
              rel="noreferrer"
            >
              Forgot Password
            </a>
          </div> */}
        </Form>
      </FormProvider>
    </div>
  );
};

export default LoginForm;
